"use client";

import { useMemo, useContext } from "react";
import type { QRProps, QREditorContextProps } from "@/contexts/QREditorContext/types";
import { QREditorContext } from "@/contexts/QREditorContext";
import { usePreviewData } from "@/features/previews/shared/hooks/usePreviewData";
import { usePreviewValidation } from "@/features/previews/shared/hooks/usePreviewValidation";
import { UsePdfDataResult, PdfTemplateTranslations } from "../types";
import type { QRPDFType } from "@/types/qr";

export const usePdfData = (props: QRProps<"pdf">): UsePdfDataResult => {
  const { data, isInitPreview: isInitialPreview } = usePreviewData(props);
  const context = useContext(QREditorContext) as QREditorContextProps<"pdf"> | null;

  const translations = props.previewTranslations?.["pdf"] as PdfTemplateTranslations;

  usePreviewValidation({
    rules: [
      {
        type: "custom",
        validator: () => !!data?.pdfFileUrl,
      },
    ],
    dependencies: [data?.pdfFileUrl],
    context,
    isInitPreview: isInitialPreview,
  });

  const {
    companyName,
    pdfDescription,
    pdfName,
    pdfFileName,
    pdfFileUrl,
    size,
    themeColor,
    websiteUrl,
    logoUrl,
  } = useMemo(() => {
    if (!data) {
      return {
        companyName: undefined,
        pdfDescription: undefined,
        pdfName: undefined,
        pdfFileName: undefined,
        pdfFileUrl: undefined,
        size: undefined,
        themeColor: undefined,
        websiteUrl: undefined,
        logoUrl: undefined,
      };
    }

    const parsedSize = data.size ? parseInt(data.size as unknown as string, 10) : undefined;

    return {
      companyName: data.companyName,
      pdfDescription: data.pdfDescription,
      pdfName: data.pdfName,
      pdfFileName: data.pdfFileName,
      pdfFileUrl: data.pdfFileUrl,
      size: !isNaN(parsedSize as number) ? parsedSize : undefined,
      themeColor: data.themeColor,
      websiteUrl: data.websiteUrl,
      logoUrl: data.logoUrl,
    };
  }, [data]);

  return {
    translations,
    isInitialPreview,
    data: data as QRPDFType | undefined,
    companyName,
    pdfDescription,
    pdfName,
    pdfFileName,
    pdfFileUrl,
    size,
    themeColor,
    websiteUrl,
    logoUrl,
  };
};
